import { Box, Stack } from '@mui/material';
import {
  useLazyListGroupsByDisplayInfoQuery,
  useLazyListItemsByDisplayInfoQuery,
} from 'api/ordering-service/ordering-service';
import ItemId from 'components/drawers/products-drawer/item-id.component';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectBaseLocaleInUpperCase } from 'redux/i18n/i18n.selectors';
import { CategoryOption } from '../fields/category-autocomplete-field/category-autocomplete-base.component';
import CategoryAutocompleteField from '../fields/category-autocomplete-field/category-autocomplete-field.component';
import { useCategoryAutocomplete } from '../fields/category-autocomplete-field/category-autocomplete.hooks';
import ProductsList from './products-list/products-list.component';
import ProductListSingleItem from './product-list-single-item/product-list-single-item.component';
import CategoryAutocompleteFieldOption from '../fields/category-autocomplete-field/category-autocomplete-field-option/category-autocomplete-field-option';

type ProductOption = CategoryOption & {
  product: any;
};

type ProductListFormProps = {
  selectAll?: boolean;
};

const ProductListForm = ({ selectAll = false }: ProductListFormProps) => {
  const { t } = useTranslation();
  const languageCode = useSelector(selectBaseLocaleInUpperCase);

  const cat = useCategoryAutocomplete<ProductOption>(
    'products',
    {
      ITEM: {
        label: t('forms.fields.productType.options.item'),
        lazyQuery: useLazyListItemsByDisplayInfoQuery(),
        queryArgs: { count: 200, page: 0, languageCode },
        transformFn: transformItem,
        inputTransformFn: inputValue => ({
          displayInfoTitle: inputValue,
        }),
      },
      GROUP: {
        label: t('forms.fields.productType.options.group'),
        lazyQuery: useLazyListGroupsByDisplayInfoQuery(),
        queryArgs: { count: 200, page: 0, languageCode },
        transformFn: transformGroup,
        inputTransformFn: inputValue => ({
          displayInfoTitle: inputValue,
        }),
      },
    },
    'ITEM',
  );

  return (
    <Stack>
      <Box className="field-container">
        <CategoryAutocompleteField
          {...cat}
          selectAll={selectAll}
          name="products"
          autoFocus
          renderOption={(props, option, { selected }) => (
            <CategoryAutocompleteFieldOption
              allSelected={cat.allSelected}
              props={props}
              option={option}
              selected={selected}
            >
              <ItemId item={option} />
            </CategoryAutocompleteFieldOption>
          )}
        />
      </Box>
      <ProductsList
        renderItem={item => <ProductListSingleItem key={item.id} item={item} />}
      />
    </Stack>
  );
};

export default ProductListForm;

export const transformItem = ({ items }) =>
  items
    .map(item => ({
      ...item,
      product: {
        displayInfoTitlesList: item.responseData.displayInfoTitles,
        externalId:
          item.responseData.idType === 'EXTERNAL_ID'
            ? item.responseData.id
            : null,
        internalId:
          item.responseData.idType === 'ID' ? item.responseData.id : null,
        type: 'ITEM',
      },
    }))
    .sort(
      (a, b) => -b.responseData.itemType.localeCompare(a.responseData.itemType),
    );

export const transformGroup = ({ groups }) =>
  groups
    .map(group => ({
      ...group,
      product: {
        displayInfoTitlesList: group.responseData.displayInfoTitles,
        externalId:
          group.responseData.idType === 'EXTERNAL_ID'
            ? group.responseData.id
            : null,
        internalId:
          group.responseData.idType === 'ID' ? group.responseData.id : null,
        type: 'GROUP',
      },
    }))
    .sort(
      (a, b) =>
        -b.responseData.groupType.localeCompare(a.responseData.groupType),
    );
